/**
 * Block-child `OLE2FRAME` render arm (@codex PR #785 round 2, F1).
 *
 * # Why this is NOT a transformed clone
 *
 * `OLE2FRAME` is stored as TWO opposite WCS corners (10/20/30 and 11/21/31)
 * and carries no rotation field — an OLE object's frame is axis-aligned in the
 * file by construction (Autodesk DXF reference; ezdxf's `OLE2Frame` implements
 * no `transform()` at all). Inside a block definition, however, the enclosing
 * INSERT may rotate it by anything: the image of the authored rectangle under
 * an oblique rotation is a ROTATED rectangle, and two opposite corners cannot
 * express one.
 *
 * The previous arm cloned the entity with the four mapped corners collapsed
 * back into their axis-aligned box. That box is a legitimate BOUNDS (it never
 * under-reports the framed region — the same never-under-report rule
 * `cad-core`'s `blockDefinitionWalk` / `wcsBoundsLeaf.oleFrameLocal` apply),
 * but it is NOT the entity's shape: a 20x10 frame inside a 45°-rotated INSERT
 * was DRAWN as the ~21.2 x 21.2 upright box around itself.
 *
 * So the frame is composed instead of cloned, the same shape the neighbouring
 * composite arms use (`renderBlockChildDimension` in `./blockFromEntity.ts`,
 * `./blockChildMultileader.ts`, `./blockChildTable.ts`): the four corners are
 * mapped by the INSERT affine and the renderable is built from THEM, so the
 * drawn quad is the exact affine image of the authored rectangle for every
 * rotation, mirror and non-uniform scale. Nothing is written back to the
 * persisted entity — the model keeps saying exactly what the file says.
 *
 * `bounds` is the AABB of those same four world corners: exact for the drawn
 * quad, still contained in the INSERT box the spatial index reserves.
 */

import type { BlockDefinition, OleFrameEntity } from "@dxf-ai-editor/drawing-model";

import type { RenderableEntity, Vec3 } from "../../types";

import type { AdapterContext } from "../adapterContext";

import { bakeBlockChildLocal, type BlockChildIdentity } from "./blockChildIdentity";
import {
  transformBlockChildPoint,
  type BlockInsertTransform,
} from "./blockInsertTransform";
import {
  oleFrameCorners,
  oleFrameRenderableFromCorners,
} from "./oleFrameFromEntity";

/**
 * The frame's four corners carried into WORLD space by a block-instance
 * affine — RAW (un-offset) WCS, the frame's true rotated rectangle.
 *
 * PUBLIC on purpose: the PDF plot converter needs the identical four points to
 * stroke the frame of an OLE child inside a plotted INSERT, and a second
 * derivation there could disagree with the screen. `null` propagates
 * {@link oleFrameCorners}'s non-finite guard.
 */
export function oleFrameWorldCorners(
  entity: Pick<OleFrameEntity, "upperLeft" | "lowerRight">,
  basePoint: Vec3,
  t: BlockInsertTransform,
): [Vec3, Vec3, Vec3, Vec3] | null {
  const local = oleFrameCorners(entity);
  if (!local) return null;
  const [a, b, c, d] = local;
  return [
    transformBlockChildPoint(a, basePoint, t),
    transformBlockChildPoint(b, basePoint, t),
    transformBlockChildPoint(c, basePoint, t),
    transformBlockChildPoint(d, basePoint, t),
  ];
}

/**
 * Render ONE block-child OLE2FRAME: place its four corners with the enclosing
 * INSERT affine and draw that quad (module doc).
 *
 * The shell (per-instance identity, AutoCAD layer-"0" inheritance, resolved
 * colour / linetype / lineweight / zOrder) comes from the SAME
 * {@link bakeBlockChildLocal} + `buildRenderableShell` pair every other arm
 * uses, so only the geometry differs from the old clone path.
 */
export function renderBlockChildOleFrame(args: {
  child: OleFrameEntity;
  basePoint: Vec3;
  t: BlockInsertTransform;
  ctx: AdapterContext;
  blocks: Record<string, BlockDefinition>;
  identity: BlockChildIdentity;
}): RenderableEntity | null {
  const { child, basePoint, t, ctx, blocks, identity } = args;
  const corners = oleFrameWorldCorners(child, basePoint, t);
  if (!corners) return null;
  const { local, adapterCtx } = bakeBlockChildLocal<OleFrameEntity>(
    child,
    identity,
    ctx,
    blocks,
  );
  return oleFrameRenderableFromCorners(local, adapterCtx, corners);
}
